import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { leoApi, leoKeys } from '../api';
import type { CreateLeoDto, UpdateLeoDto, MergeLeoDto, LeoFilters } from '../types';

// Case 360° (Stage 8) — LEO directory list.
export function useLeoEntries(filters: LeoFilters) {
  return useQuery({
    queryKey: leoKeys.list(filters),
    queryFn: () => leoApi.getEntries(filters),
    staleTime: 60 * 1000,
  });
}

// Case 360° (Stage 8) — LEO create / update / verify / merge / archive.
export function useLeoMutations() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: leoKeys.all });
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateLeoDto) => leoApi.createEntry(data),
    onSuccess: () => {
      toast.success(t('leo.createSuccess'));
      invalidate();
    },
    // 409 (duplicate warning/block) is handled by the dialog, not toasted here.
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: UpdateLeoDto }) =>
      leoApi.updateEntry(id, data),
    onSuccess: () => {
      toast.success(t('leo.updateSuccess'));
      invalidate();
    },
  });

  const verifyMutation = useMutation({
    mutationFn: ({ id }: { id: string | number }) => leoApi.verifyEntry(id),
    onSuccess: () => {
      toast.success(t('leo.verifySuccess'));
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('leo.verifyError'))),
  });

  const mergeMutation = useMutation({
    mutationFn: (data: MergeLeoDto) => leoApi.mergeEntries(data),
    onSuccess: () => {
      toast.success(t('leo.mergeSuccess'));
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('leo.mergeError'))),
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id }: { id: string | number }) => leoApi.archiveEntry(id),
    onSuccess: () => {
      toast.success(t('leo.archiveSuccess'));
      invalidate();
    },
    onError: (e: unknown) =>
      toast.error(getApiErrorMessage(e, t('leo.archiveError'))),
  });

  return {
    createMutation,
    updateMutation,
    verifyMutation,
    mergeMutation,
    archiveMutation,
  };
}
